/**
 * Venue picker dialog (#689): searchable outcomeMeta venues with outcome counts, or "All venues". Opened from the
 * header Settings menu (#2242), which shows the current scope and hides the entry when the build locks the venue.
 */
import { useMemo, useState } from 'react';
import Modal from '../components/Modal';
import { useOutcomeMeta } from '../account/useMarketData';
import { useEnv } from '../config/env';
import { foldSearch, venueCounts, toItem } from '../catalog/catalog';
import { venueLabel } from './scope';
import { useVenueScope } from './useVenueScope';
import Msg from '../i18n/Msg';
import { networkWord } from '../i18n/common';
import { useT } from '../i18n/useT';

const row = 'flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-start text-sm';

export default function VenueDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { env } = useEnv();
  const scope = useVenueScope();
  const t = useT();
  const [q, setQ] = useState('');
  const meta = useOutcomeMeta(open);
  const outcomes = meta?.outcomes;
  const counts = useMemo(() => venueCounts((outcomes ?? []).map((o) => toItem(o, undefined))), [outcomes]);
  if (scope.isLocked) return null;

  const total = outcomes?.length;
  const needle = foldSearch(q.trim());
  const list = counts.filter(([v]) => !needle || foldSearch(venueLabel(v)).includes(needle));
  // Keep the current venue pickable even when it has no outcomes on this env.
  if (scope.venue !== null && !counts.some(([v]) => v === scope.venue) && (!needle || scope.venue.includes(needle))) {
    list.unshift([scope.venue, 0]);
  }
  const close = () => {
    onClose();
    setQ('');
  };
  const pick = (v: string | null) => {
    scope.setVenue(v);
    close();
  };
  const scoped = scope.venue !== null;

  return (
    <>
      <Modal open={open} onClose={close} title={t('venue.title')}>
        <div className="space-y-3">
          <p className="text-xs text-muted">
            <Msg
              k="venue.body"
              args={{ network: networkWord(env.name), param: <code>?venue=&lt;id&gt;</code>, paramAll: <code>?venue=all</code> }}
            />
          </p>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('venue.search')}
            aria-label={t('venue.search')}
            className="w-full rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm placeholder:text-muted"
          />
          <ul className="max-h-[50vh] space-y-0.5 overflow-y-auto" aria-label={t('venue.list')}>
            <li>
              <button
                type="button"
                aria-pressed={!scoped}
                onClick={() => pick(null)}
                className={`${row} ${!scoped ? 'bg-accent/15 text-accent' : 'hover:bg-surface-2'}`}
              >
                <span>{t('venue.all')}</span>
                <span className="num text-xs opacity-70">{total ?? ''}</span>
              </button>
            </li>
            {!meta && <li className="px-3 py-2 text-xs text-muted">{t('venue.loading')}</li>}
            {list.map(([v, n]) => {
              const on = scope.venue === v;
              return (
                <li key={v}>
                  <button
                    type="button"
                    aria-pressed={on}
                    onClick={() => pick(v)}
                    data-testid={`venue-option-${v}`}
                    className={`${row} ${on ? 'bg-accent/15 text-accent' : 'hover:bg-surface-2'}`}
                  >
                    <span className="truncate" translate="no">
                      {venueLabel(v)}
                    </span>
                    <span className="num text-xs opacity-70">{n}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {scope.buildVenue !== null && (scope.source === 'stored' || scope.source === 'url') && (
            <button type="button" className="text-xs text-accent hover:underline" onClick={() => { scope.resetVenue(); close(); }}>
              {t('venue.reset', { venue: scope.buildVenue })}
            </button>
          )}
        </div>
      </Modal>
    </>
  );
}
